import { AwsConfig as AwsConstants } from '@services/Constants';

import BaseService, { RequestInterface } from '@services/Base';
import IPagination from '@interfaces/IPagination';
import Ticket from '@interfaces/ehss/ticket';
import TicketPaginationQuery from '@interfaces/ehss/ticket-pagination-query';
import TaskflowValue from '@interfaces/taskflow-value';
import DashboardQuery from '@interfaces/ehss/dashboard-query';
import { Dashboard } from '@interfaces/ehss/Dashboard';
import IProcessValue from '@components/FormFlow/Interfaces/IProcessValue';

export default class TicketGlobalService extends BaseService {
    constructor () {
        super(AwsConstants.API_CONFIG, 'ticket');
    }

    async getById (id: string, unit: string, fields?: string[]): Promise<Ticket> {
        const params: RequestInterface = {
            queryStringParameters: super.setFields(fields)
        };
        params.queryStringParameters['unit'] = unit;
        const res = await super.get<Ticket>(id, params);
        return res;
    }
    async notificationExec (id: string, eventId: string): Promise<void> {
        await super.patch<void>(`${id}/notification/${eventId}`);
    }
    async processExec (id: string, eventId: string, data: IProcessValue): Promise<void> {
        const params: RequestInterface = {
            body: data
        };
        await super.patch<void>(`${id}/process/${eventId}`, params);
    }
    async start (workflowId: string, distributorId: string, unitId: string, data: TaskflowValue): Promise<Ticket> {
        const params: RequestInterface = {
            queryStringParameters: { distributor: distributorId, unit: unitId },
            body: data
        };
        const res = await super.patch<Ticket>(`workflow/${workflowId}`, params);
        return res;
    }

    async paging (query: TicketPaginationQuery, fields?: string[]): Promise<IPagination<Ticket>> {
        const params: RequestInterface = {
            queryStringParameters: super.setFields(fields)
        };
        if (query.unit) {
            params.queryStringParameters['unit'] = query.unit;
        }
        if (query.limit) {
            params.queryStringParameters['limit'] = query.limit;
        }
        if (query.offset) {
            params.queryStringParameters['offset'] = query.offset;
        }
        if (query.from != null) {
            params.queryStringParameters['from'] = query.from;
        }
        if (query.to != null) {
            params.queryStringParameters['to'] = query.to;
        }
        if (query.status != null) {
            params.queryStringParameters['status'] = query.status;
        }
        if (query.orderBy) {
            params.queryStringParameters['orderBy'] = query.orderBy; // "modified" para cronologico E 'risk' para risco
        }
        if (query.disable) {
            params.queryStringParameters['disable'] = query.disable; // "modified" para cronologico E 'risk' para risco
        }
        if (query.fieldId && query.fieldValue) {
            params.queryStringParameters['field'] = query.fieldId;
            params.queryStringParameters['value'] = query.fieldValue;
        }

        const res = await super.get<IPagination<Ticket>>(``, params);
        return res;
    }

    async pagingByAuditor (id: string, processId: string, query: TicketPaginationQuery, fields?: string[]): Promise<IPagination<Ticket>> {
        const params: RequestInterface = {
            queryStringParameters: super.setFields(fields)
        };

        if (query.unit) {
            params.queryStringParameters['unit'] = query.unit;
        }
        if (query.limit) {
            params.queryStringParameters['limit'] = query.limit;
        }
        if (query.offset) {
            params.queryStringParameters['offset'] = query.offset;
        }

        params.queryStringParameters['process'] = processId;
        params.queryStringParameters['auditor'] = id;


        const res = await super.get<IPagination<Ticket>>(``, params);
        return res;
    }
    async pagingByAudited (id: string, processId: string, query: TicketPaginationQuery, fields?: string[]): Promise<IPagination<Ticket>> {
        const params: RequestInterface = {
            queryStringParameters: super.setFields(fields)
        };

        if (query.unit) {
            params.queryStringParameters['unit'] = query.unit;
        }
        if (query.limit) {
            params.queryStringParameters['limit'] = query.limit;
        }
        if (query.offset) {
            params.queryStringParameters['offset'] = query.offset;
        }
        params.queryStringParameters['process'] = processId;
        params.queryStringParameters['audited'] = id;


        const res = await super.get<IPagination<Ticket>>(``, params);
        return res;
    }

    async my (unitId: string, query: TicketPaginationQuery, fields?: string[]): Promise<IPagination<Ticket>> {
        const params: RequestInterface = {
            queryStringParameters: super.setFields(fields)
        };
        params.queryStringParameters['unit'] = unitId;
        if (query.limit) {
            params.queryStringParameters['limit'] = query.limit;
        }
        if (query.offset) {
            params.queryStringParameters['offset'] = query.offset;
        }

        const res = await super.get<IPagination<Ticket>>('my', params);
        return res;
    }

    

    async getDashboard (query: DashboardQuery): Promise<Dashboard[]> {
        const params: RequestInterface = {
            queryStringParameters: {}
        };

        if (query.unit) {
            params.queryStringParameters['unit'] = query.unit;
        }
        if (query.from) {
            params.queryStringParameters['from'] = query.from;
        }
        if (query.to) {
            params.queryStringParameters['to'] = query.to;
        }
        if (query.field) {
            params.queryStringParameters['field'] = query.field;
        }
        if (query.value) {
            params.queryStringParameters['value'] = query.value;
        }
        if (query.process) {
            params.queryStringParameters['process'] = query.process;
        }
        if (query.auditor) {
            params.queryStringParameters['auditor'] = query.auditor;
        }
        if (query.audited) {
            params.queryStringParameters['audited'] = query.audited;
        }
        if (query.auditedOut) {
            params.queryStringParameters['auditedOut'] = query.auditedOut;
        }
        if (query.auditorGuest) {
            params.queryStringParameters['auditorGuest'] = query.auditorGuest;
        }
        if (query.status) {
            params.queryStringParameters['status'] = query.status;
        }
        const res = await super.get<Dashboard[]>('dashboard', params);
        return res;
    }

    async getDashboardRisk (query: DashboardQuery): Promise<Dashboard[]> {
        const params: RequestInterface = {
            queryStringParameters: {}
        };

        if (query.unit) {
            params.queryStringParameters['unit'] = query.unit;
        }
        if (query.from) {
            params.queryStringParameters['from'] = query.from;
        }
        if (query.to) {
            params.queryStringParameters['to'] = query.to;
        }
        if (query.field) {
            params.queryStringParameters['field'] = query.field;
        }
        if (query.value) {
            params.queryStringParameters['value'] = query.value;
        }
        if (query.process) {
            params.queryStringParameters['process'] = query.process;
        }
        if (query.auditor) {
            params.queryStringParameters['auditor'] = query.auditor;
        }
        if (query.audited) {
            params.queryStringParameters['audited'] = query.audited;
        }
        if (query.auditedOut) {
            params.queryStringParameters['auditedOut'] = query.auditedOut;
        }
        if (query.auditorGuest) {
            params.queryStringParameters['auditorGuest'] = query.auditorGuest;
        }
        if (query.status) {
            params.queryStringParameters['status'] = query.status;
        }
        const res = await super.get<Dashboard[]>('dashboard/risk', params);
        return res;
    }
    async filetTicket (id: string, data: Ticket, isField: boolean): Promise<Ticket> {
      const params: RequestInterface = {
        body: data,
    };
        const res = await super.put<Ticket>(`${id}/disable/${isField}`, params);
        return res;
    }
}